import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { Badge } from '@/lib/base/badges/badges';

interface Proposal {
  id: number;
  title: string;
  resolution: string;
}

interface DynamicView {
  id: number;
  title: string;
}

interface ChainResultsPreviewProps {
  proposals?: Proposal[];
  dynamicViews?: DynamicView[];
  onOpenDynamicViewSidebar?: (id: number) => void;
}

/**
 * Shared component for displaying proposals and dynamic views in chain result cards.
 * Used by both CRM chat and main reasoning chat to ensure consistent UI.
 * Replaces ChainArtifactsPreview - artifacts are no longer displayed.
 */
export function ChainResultsPreview({
  proposals = [],
  dynamicViews = [],
  onOpenDynamicViewSidebar,
}: ChainResultsPreviewProps) {
  const { openProposal, openProposalId } = useProposalSidebar();

  if (proposals.length === 0 && dynamicViews.length === 0) {
    return null;
  }

  // Get resolution-based styles for proposals using UntitledUI tokens.
  // Card background stays neutral, only icon reflects status.
  // bg-bg-primary: top-level chat module cards share the shell canon
  // (VARIANT_SHELL); nested icon boxes take bg-bg-secondary — inner elements
  // sit one surface level below their card.
  const getResolutionStyles = (resolution: string) => {
    switch (resolution) {
      case 'approved':
        return {
          card: 'bg-bg-primary hover:bg-bg-secondary border-border-secondary',
          text: 'text-fg-primary',
          iconBox:
            'bg-utility-success-50 dark:bg-utility-success-950 border-utility-success-200 dark:border-utility-success-700',
          iconColor: 'text-utility-success-600 dark:text-utility-success-400',
        };
      case 'rejected':
      case 'failed':
        return {
          card: 'bg-bg-primary hover:bg-bg-secondary border-border-secondary',
          text: 'text-fg-primary',
          iconBox:
            'bg-utility-error-50 dark:bg-utility-error-950 border-utility-error-200 dark:border-utility-error-700',
          iconColor: 'text-utility-error-600 dark:text-utility-error-400',
        };
      case 'withdrawn':
        return {
          card: 'bg-bg-primary hover:bg-bg-secondary border-border-secondary',
          text: 'text-fg-secondary',
          iconBox: 'bg-bg-secondary border-border-secondary',
          iconColor: 'text-fg-tertiary',
        };
      default: // pending
        return {
          card: 'bg-bg-primary hover:bg-bg-secondary border-border-secondary',
          text: 'text-fg-primary',
          iconBox: 'bg-bg-secondary border-border-secondary',
          iconColor: 'text-fg-tertiary',
        };
    }
  };

  return (
    <>
      {/* Proposal Previews */}
      {proposals.length > 0 && (
        <div className='mt-3 space-y-2'>
          {proposals.map(proposal => {
            const styles = getResolutionStyles(proposal.resolution);
            // Same "this is what the panel is showing" treatment as the
            // builder's InlineProposalCard: brand ring + badge.
            const isSelected = proposal.id === openProposalId;
            return (
              <div
                key={proposal.id}
                onClick={() => openProposal(proposal.id)}
                className={`border rounded-xl p-4 cursor-pointer transition-all ${styles.card} ${
                  isSelected
                    ? 'ring-2 ring-utility-brand-600 dark:ring-utility-brand-500'
                    : ''
                }`}
              >
                <div className='flex items-center gap-4'>
                  <div
                    className={`w-10 h-10 border rounded-lg flex items-center justify-center ${styles.iconBox}`}
                  >
                    <svg
                      className={`w-5 h-5 ${styles.iconColor}`}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M13 10V3L4 14h7v7l9-11h-7z'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 min-w-0 flex items-center gap-2'>
                    <p
                      className={`text-sm font-semibold truncate ${styles.text}`}
                    >
                      {proposal.title}
                    </p>
                    {isSelected && (
                      <Badge type='pill-color' color='brand' size='sm'>
                        Showing in panel
                      </Badge>
                    )}
                  </div>
                  <svg
                    className='w-6 h-6 text-fg-quaternary'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dynamic View Previews */}
      {dynamicViews.length > 0 && (
        <div className='mt-3 space-y-2'>
          {dynamicViews.map(view => (
            <div
              key={view.id}
              onClick={
                onOpenDynamicViewSidebar
                  ? () => onOpenDynamicViewSidebar(view.id)
                  : undefined
              }
              className={`bg-bg-primary border border-border-secondary rounded-xl p-4 transition-shadow ${
                onOpenDynamicViewSidebar ? 'cursor-pointer hover:shadow-sm' : ''
              }`}
            >
              <div className='flex items-center gap-4'>
                <div className='w-10 h-10 bg-bg-secondary border border-border-secondary rounded-lg flex items-center justify-center'>
                  <svg
                    className='w-6 h-6 text-fg-tertiary'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z'
                    />
                  </svg>
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-semibold text-fg-primary truncate'>
                    {view.title}
                  </p>
                </div>
                {onOpenDynamicViewSidebar && (
                  <svg
                    className='w-6 h-6 text-fg-quaternary'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
